import { Prose, Text } from '@payaca/ambrosia';
import * as clipboard from 'clipboard-polyfill/text';
import React, { type FC, useCallback, useEffect, useState } from 'react';
import { defaultUrlTransform } from 'react-markdown';

import MarkdownCore from '@payaca/components/markdownLabel/MarkdownCore';
import UntitledIcon from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';
import CodeBlock from './CodeBlock';
import { EntityLink } from './EntityLink';
import { ToolStatusIndicator } from './ToolStatusIndicator';
import type { ChatMessage as ChatMessageType, ContentBlock } from './types';

interface Props {
  message: ChatMessageType;
}

/**
 * Custom URL transform that allows payaca:// URIs while keeping default security.
 * react-markdown's default transform strips non-standard protocols.
 */
function chatUrlTransform(url: string): string {
  if (url.startsWith('payaca://')) {
    return url;
  }
  return defaultUrlTransform(url);
}

const markdownComponents = {
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) =>
    href ? (
      <EntityLink href={href}>{children}</EntityLink>
    ) : (
      <span>{children}</span>
    ),
  pre: ({ children }: { children?: React.ReactNode }) => (
    <CodeBlock>{children}</CodeBlock>
  ),
  code: ({ children }: { children?: React.ReactNode }) => (
    // oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- Relative to the parent font size
    <code className="rounded-md border border-gray-200 bg-gray-100 px-1.5 py-0.5 text-[0.875em] font-semibold text-gray-800">
      {children}
    </code>
  ),
  table: ({ children }: { children?: React.ReactNode }) => (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="min-w-full text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }: { children?: React.ReactNode }) => (
    <thead className="border-b bg-gray-50">{children}</thead>
  ),
  tbody: ({ children }: { children?: React.ReactNode }) => (
    <tbody className="divide-y divide-gray-100">{children}</tbody>
  ),
  tr: ({ children }: { children?: React.ReactNode }) => (
    <tr className="even:bg-gray-50">{children}</tr>
  ),
  th: ({ children }: { children?: React.ReactNode }) => (
    <th className="px-3 py-2 text-left font-medium text-gray-700">
      {children}
    </th>
  ),
  td: ({ children }: { children?: React.ReactNode }) => (
    <td className="px-3 py-2 text-gray-600">{children}</td>
  ),
};

const MarkdownChat: FC<{ markdown: string }> = (props) => {
  const { markdown } = props;

  return (
    <Prose className="max-w-none [&>*:not(:first-child)]:mt-3">
      <MarkdownCore
        markdown={markdown}
        urlTransform={chatUrlTransform}
        components={markdownComponents}
      />
    </Prose>
  );
};

const ThinkingIndicator: FC = () => (
  <div className="flex items-center gap-2 py-1 text-sm text-gray-500">
    <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
    Dave is thinking…
  </div>
);

const StaleToolMessage: FC = () => (
  <Text level="supporting" colour="muted">
    Something went wrong.
  </Text>
);

const MessageCopyButton: FC<{ content: string }> = ({ content }) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);

  const handleCopy = useCallback(async () => {
    await clipboard.writeText(content);
    setCopied(true);
  }, [content]);

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="flex cursor-pointer items-center gap-1 rounded-full border border-gray-200 bg-white px-2 py-1 text-xs text-gray-500 [@media(hover:hover)]:opacity-0 shadow-sm transition-opacity group-hover:opacity-100"
    >
      <UntitledIcon className="h-3 w-3" name={copied ? 'check' : 'copy-07.3'} />
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
};

function getFullTextContent(message: ChatMessageType): string {
  if (message.contentBlocks?.length) {
    return message.contentBlocks
      .filter(
        (b): b is Extract<ContentBlock, { type: 'text' }> => b.type === 'text'
      )
      .map((b) => b.text)
      .join('\n\n');
  }
  return message.content;
}

export const ChatMessage: FC<Props> = ({ message }) => {
  const isUser = message.role === 'user';

  const hasRunningTools = message.toolUses?.some((t) => t.status === 'running');

  // Customer-friendly description of what Dave is doing right now:
  // the most recent running tool's label, falling back to the most recent
  // tool's label while the stream is still going.
  const tools = message.toolUses ?? [];
  const activeToolLabel = (
    [...tools].reverse().find((t) => t.status === 'running') ?? tools.at(-1)
  )?.friendlyLabel;

  // Render assistant content blocks in order (interleaved text and tools)
  const renderContentBlocks = (blocks: ContentBlock[]) => {
    const lastBlock = blocks[blocks.length - 1];
    const endsWithTool = lastBlock?.type === 'tool';

    const rendered = blocks.map((block, index) => {
      if (block.type === 'text') {
        return <MarkdownChat key={`text-${index}`} markdown={block.text} />;
      }

      return null;
    });

    if (!message.isStreaming && hasRunningTools) {
      // Stream ended while tools were still running
      rendered.push(<StaleToolMessage key="stale-tools" />);
    } else if (message.isStreaming && endsWithTool) {
      // Still streaming and last content is a tool — keep indicator visible
      // until the next text block or tool arrives
      rendered.push(
        <ToolStatusIndicator key="tool-status" label={activeToolLabel} />
      );
    }

    return rendered;
  };

  const hasContent =
    message.content || (message.contentBlocks?.length ?? 0) > 0;

  if (isUser) {
    return (
      <div className="flex justify-end">
        <div className="rounded-2xl bg-purple-100 px-4 py-2.5 text-purple-900 whitespace-pre-wrap">
          <Text>{message.content}</Text>
        </div>
      </div>
    );
  }

  const fullText = getFullTextContent(message);
  const showCopyButton = !message.isStreaming && fullText.length > 0;

  let toolIndicator: React.ReactNode = null;
  if (!message.isStreaming && hasRunningTools) {
    toolIndicator = <StaleToolMessage />;
  } else if (message.isStreaming && (message.toolUses?.length ?? 0) > 0) {
    toolIndicator = <ToolStatusIndicator label={activeToolLabel} />;
  }

  return (
    <div className={clstx('group relative')}>
      <div className="space-y-3">
        {message.contentBlocks?.length ? (
          renderContentBlocks(message.contentBlocks)
        ) : (
          <>
            {message.content && <MarkdownChat markdown={message.content} />}
            {toolIndicator}
          </>
        )}
      </div>

      {showCopyButton && (
        <div className="absolute bottom-0 right-0 z-10">
          <MessageCopyButton content={fullText} />
        </div>
      )}

      {message.isStreaming && !hasContent && <ThinkingIndicator />}
    </div>
  );
};
